<template>
  <div class="md:flex md:items-center md:justify-between mb-3">
    <div class="flex-1 min-w-0">
      <h2 class="text-xl font-bold tracking-tight text-foreground lg:text-2xl">
        Transaction
      </h2>
    </div>
    <div class="mt-4 flex md:mt-0 md:ml-4">
      <Combobox as="div" v-model="bankFilter" @update:modelValue="saveToLocalStorage('bankFilter', $event)">
        <div class="relative">
          <ComboboxInput
              class="w-64 rounded-md border-0 bg-white py-1.5 pl-10 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              @change="bankQuery = $event.target.value"
              :display-value="(bank) => bank || 'Toutes les banques'"
              placeholder="Toutes les banques"
          />
          <ComboboxButton class="absolute inset-y-0 left-0 flex items-center rounded-l-md px-2 focus:outline-none">
            <CreditCardIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
          </ComboboxButton>
          <ComboboxButton class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
            <ChevronDownIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
          </ComboboxButton>

          <ComboboxOptions v-if="filteredBanks.length > 0" class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            <ComboboxOption value="" v-slot="{ active, selected }">
              <li :class="['relative cursor-default select-none py-2 pl-3 pr-9', active ? 'bg-indigo-600 text-white' : 'text-gray-900']">
                <span :class="['block truncate', selected && 'font-semibold']">Toutes les banques</span>
                <span v-if="selected" :class="['absolute inset-y-0 right-0 flex items-center pr-4', active ? 'text-white' : 'text-indigo-600']">
                  <CheckIcon class="h-5 w-5" aria-hidden="true" />
                </span>
              </li>
            </ComboboxOption>
            <ComboboxOption v-for="bank in filteredBanks" :key="bank" :value="bank" v-slot="{ active, selected }">
              <li :class="['relative cursor-default select-none py-2 pl-3 pr-9', active ? 'bg-indigo-600 text-white' : 'text-gray-900']">
                <span :class="['block truncate', selected && 'font-semibold']">{{ bank }}</span>
                <span v-if="selected" :class="['absolute inset-y-0 right-0 flex items-center pr-4', active ? 'text-white' : 'text-indigo-600']">
                  <CheckIcon class="h-5 w-5" aria-hidden="true" />
                </span>
              </li>
            </ComboboxOption>
          </ComboboxOptions>
        </div>
      </Combobox>

      <div class="relative rounded-md shadow-sm ml-3">
        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <SearchIcon
              class="h-5 w-5 text-gray-400"
              aria-hidden="true"
          />
        </div>
        <form id="search">
          <input
              v-model="searchQuery"
              type="text"
              name="query"
              class="inline-flex focus:ring-indigo-500  px-4  py-2 focus:border-indigo-500 block w-full min-w-0  pl-10 sm:text-sm border-gray-300 rounded-md"
              @input="saveToLocalStorage('filterKey', $event.target.value)"
          >
        </form>
      </div>

      <button
          class="ml-3 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          :class="groupBySupplier ? 'text-indigo-600 border-indigo-500' : 'text-gray-700'"
          type="button"
          @click="toggleGroupBySupplier"
      >
        {{ groupBySupplier ? 'Liste simple' : 'Grouper par fournisseur' }}
      </button>

      <button
          class="ml-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          type="button"
          @click="showAdd = true"
      >
        Ajouter
      </button>
    </div>
  </div>
  <div class="sm:hidden">
    <label
        class="sr-only"
        for="tabs"
    >Select a tab</label>
    <!-- Use an "onChange" listener to redirect the user to the selected tab URL. -->
    <select
        id="tabs"
        class="block w-full focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md"
        name="tabs"
    >
      <option
          v-for="tab in tabs"
          :key="tab.name"
          :selected="tab.current"
      >
        {{ tab.name }}
      </option>
    </select>
  </div>

  <!-- Statistics Banner -->
  <div class="mt-4 mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
    <div class="bg-gradient-to-br from-indigo-50 to-indigo-100 overflow-hidden rounded-xl border border-indigo-200">
      <div class="px-4 py-3">
        <dt class="text-xs font-medium text-indigo-600 uppercase tracking-wide">{{ statisticsTitle }}</dt>
        <dd class="mt-1 text-2xl font-bold text-indigo-900">{{ statistics.count }}</dd>
      </div>
    </div>
    <div class="bg-gradient-to-br from-emerald-50 to-emerald-100 overflow-hidden rounded-xl border border-emerald-200">
      <div class="px-4 py-3">
        <dt class="text-xs font-medium text-emerald-600 uppercase tracking-wide">Entrées</dt>
        <dd class="mt-1 text-2xl font-bold text-emerald-900">+{{ statistics.inflow }} €</dd>
      </div>
    </div>
    <div class="bg-gradient-to-br from-red-50 to-red-100 overflow-hidden rounded-xl border border-red-200">
      <div class="px-4 py-3">
        <dt class="text-xs font-medium text-red-600 uppercase tracking-wide">Sorties</dt>
        <dd class="mt-1 text-2xl font-bold text-red-900">−{{ statistics.outflow }} €</dd>
      </div>
    </div>
    <div class="bg-gradient-to-br from-blue-50 to-blue-100 overflow-hidden rounded-xl border border-blue-200">
      <div class="px-4 py-3">
        <dt class="text-xs font-medium text-blue-600 uppercase tracking-wide">Net</dt>
        <dd class="mt-1 text-2xl font-bold" :class="statistics.net >= 0 ? 'text-blue-900' : 'text-red-700'">{{ statistics.net >= 0 ? '+' : '' }}{{ statistics.net.toFixed(2) }} €</dd>
      </div>
    </div>
  </div>

  <TransactionMonthlyChart v-if="showChart" :transactions="rawTransactions"/>
  <div class="mt-0 flex flex-col">
    <div class="-my-2 -mx-4 overflow-x-hidden sm:-mx-6 lg:-mx-8 grid mt-1">
      <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
        <!-- Onglets de statut, fusionnés avec le tableau (haut de carte) -->
        <div class="hidden sm:block">
          <nav aria-label="Tabs"
               class="flex space-x-6 rounded-t-lg border border-b-0 border-gray-200 bg-gray-50 px-4">
            <router-link
                v-for="tab in tabs"
                :key="tab.name"
                :aria-current="tab.current ? 'page' : undefined"
                :class="[tab.href === $route.path ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'group inline-flex items-center py-2.5 px-1 border-b-2 font-medium text-sm']"
                :href="tab.href"
                :to="tab.href"
            >
              <component
                  :is="tab.icon"
                  :class="[tab.href === $route.path ? 'text-indigo-500' : 'text-gray-400 group-hover:text-gray-500', '-ml-0.5 mr-1.5 h-4 w-4']"
                  aria-hidden="true"
              />
              <span>{{ tab.name }}</span>
            </router-link>
          </nav>
        </div>
        <div class="overflow-hidden border border-gray-200 shadow md:rounded-b-lg">
          <InvoiceGrid
              :data1="gridData"
              :columns="gridColumns"
              :columns-displayed="gridColumnsDisplayed"
              :filter-key="searchQuery"
              :bank-filter="bankFilter"
              :page="1"
              :per-page="25"
              :pages="[]"
              :show-import-button="showImportButton"
              :group-by-supplier="groupBySupplier"
              :fetch-done="fetchDone"
              @openInvoiceUpload="openInvoiceUpload"
          />
        </div>
      </div>
    </div>
  </div>
  <TransitionRoot as="template" :show="showAdd" @close="showAdd = false">
    <TransactionAdd></TransactionAdd>
  </TransitionRoot>
  <TransitionRoot as="template" :show="showUpload" @close="showUpload = false">
    <InvoiceUpload :preselectedCompanyId="selectedCompanyId"></InvoiceUpload>
  </TransitionRoot>
</template>

<script setup>

import {CreditCardIcon, OfficeBuildingIcon, SearchIcon, UserIcon, UsersIcon, ChevronDownIcon, CheckIcon} from '@heroicons/vue/solid'
import {TransitionRoot, Combobox, ComboboxInput, ComboboxButton, ComboboxOptions, ComboboxOption} from '@headlessui/vue'


const tabs = [
  {name: 'Toutes', href: '/transaction', icon: UserIcon, current: false},
  {name: 'En attente', href: '/transaction/backlog', icon: UsersIcon, current: true},
  {name: 'Associée', href: '/transaction/linked', icon: OfficeBuildingIcon, current: false},
  {name: 'Sans justificatif', href: '/transaction/deleted', icon: CreditCardIcon, current: false},
  {name: 'Remboursée', href: '/transaction/status/3', icon: CheckIcon, current: false},
  {name: 'Compte Associé LA', href: '/transaction/status/4', icon: UserIcon, current: false},
  {name: 'Compte Associé BR', href: '/transaction/status/5', icon: UserIcon, current: false},
]

</script>


<script>
import InvoiceGrid from './TransactionGrid.vue'
import axios from "axios";
import moment from 'moment'
import TransactionAdd from './TransactionAdd.vue'
import InvoiceUpload from '../invoice/InvoiceUpload.vue'
import TransactionMonthlyChart from './TransactionMonthlyChart.vue'


export default {
  components: {
    InvoiceGrid,
    InvoiceUpload,
    TransactionMonthlyChart
  },
  props: {
    endpoint: String,
    showImportButton: {
      type: Boolean,
      default: false
    },
    showChart: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    showAdd: false,
    showUpload: false,
    rawTransactions: [],
    selectedCompanyId: null,
    groupBySupplier: false,
    bankFilter: '',
    bankQuery: '',
    gridColumnsDisplayed: {
      from_name: "Fournisseur",
      description: "Description",
      status_name: "Statut",
      date: "Date",
      amount: "Montant",
      bank: "Banque",
      button: ""
    },
    fetchDone: false,
    gridColumns: ['from_name', 'description', 'status_name', 'date', 'amount', 'bank'],
    gridData: [],
    searchQuery: '',
    polling: null,
  }),
  computed: {
    statisticsTitle() {
      return {
        '/transaction': 'Transactions',
        '/transaction/backlog': 'Transactions en attente',
        '/transaction/linked': 'Transactions associées',
        '/transaction/deleted': 'Sans justificatif',
        '/transaction/status/3': 'Transactions remboursées',
        '/transaction/status/4': 'Compte Associé LA',
        '/transaction/status/5': 'Compte Associé BR',
      }[this.endpoint] || 'Transactions';
    },
    statistics() {
      let inflow = 0;
      let outflow = 0;
      this.rawTransactions.forEach((t) => {
        const amount = parseFloat(t.amount) || 0;
        if (amount >= 0) inflow += amount;
        else outflow += Math.abs(amount);
      });
      return {
        count: this.rawTransactions.length,
        inflow: inflow.toFixed(2),
        outflow: outflow.toFixed(2),
        net: inflow - outflow,
      };
    },
    uniqueBanks() {
      const banks = this.gridData
        .map(item => item.bank)
        .filter(bank => bank != null);
      return [...new Set(banks)].sort();
    },
    filteredBanks() {
      return this.bankQuery === ''
        ? this.uniqueBanks
        : this.uniqueBanks.filter((bank) => {
            return bank.toLowerCase().includes(this.bankQuery.toLowerCase())
          })
    }
  },
  mounted() {
    this.searchQuery = this.getFromLocalStorage('filterKey')
    this.groupBySupplier = this.getFromLocalStorage('groupBySupplier') === 'true'
    this.bankFilter = this.getFromLocalStorage('bankFilter') || ''
    this.fetchTransactionData()
    this.intervalFetchData()
  },
  beforeUnmount() {
    clearInterval(this.polling)
  },
  methods: {
    intervalFetchData: function () {
      this.polling = setInterval(() => {
        this.fetchTransactionData();
      }, 10000);
    },
    saveToLocalStorage(item, value) {
      localStorage.setItem(item, value);
    },
    getFromLocalStorage(item) {
      return localStorage.getItem(item);
    },
    async fetchTransactionData() {
      const token = await this.$auth.getAccessTokenSilently();
      const HTTP = axios.create({
        baseURL: `https://billing.clafoutis.eu/api`,
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      HTTP.get(this.endpoint)
          .then(response => {
            const {transactions} = response.data
            this.rawTransactions = transactions
            const gridData = []
            transactions.forEach((value) => {
              gridData.push({
                id: value['id'].substr(0, 10),
                from_name: value['from_name'] ? value['from_name'].substring(0, 30) : null,
                description: (value['description'] || value['from_name']) ? (value['description'] || value['from_name']).substring(0, 30) : null,
                amount: value['amount'] ? value['amount'] : null,
                bank: value['bank'] ? value['bank']['name'] : null,
                date: value['date'] ? moment(value['date']).format('YYYY-MM-DD') : null,
                created_at: value['created_at'] ? moment(value['created_at']).format('YYYY-MM-DD') : null,
                has_invoice: value['has_invoice'] ?? null,
                status_name: this.transactionStatus(value).name,
                status_color: this.transactionStatus(value).color,
                local_amount: value['local_amount'] ? value['local_amount'] : null,
                local_currency: value['local_currency'] ? value['local_currency'] : null,
                button: value['id'],
                company_name: value['company'] ? value['company']['name'] : null,
                company_id: value['company'] ? value['company']['id'] : null,
                company_avatar: value['company'] && value['company']['avatar'] != null ? value['company']['avatar'] : 'https://cdn1.iconfinder.com/data/icons/unicons-line-vol-2/24/cloud-question-512.png',
                company_website: value['company'] && value['company']['website'] != null ? value['company']['website'] : null,
                invoices: (value['invoice'] || []).map(inv => ({
                  id: inv['id'],
                  amount: inv['amount'],
                  company_name: inv['company'] ? inv['company']['name'] : null,
                  company_avatar: inv['company'] && inv['company']['avatar'] != null ? inv['company']['avatar'] : null,
                })),

              })
            });

            // Trier par date, plus récente en haut
            gridData.sort((a, b) => {
              if (!a.date) return 1;
              if (!b.date) return -1;
              return b.date.localeCompare(a.date);
            });

            if (gridData !== this.gridData) {
              this.gridData = gridData
            }
            this.fetchDone = true
          }).catch(e => {
        console.log(e)
      })
    },
    transactionStatus(value) {
      switch (value['has_invoice']) {
        case 0: return { name: 'Sans justificatif', color: 'gray' };
        case 3: return { name: 'Remboursée', color: 'blue' };
        case 4: return { name: 'Compte Associé LA', color: 'purple' };
        case 5: return { name: 'Compte Associé BR', color: 'purple' };
        default: return (value['invoice'] || []).length
          ? { name: 'Associée', color: 'green' }
          : { name: 'En attente', color: 'yellow' };
      }
    },
    truncate(v) {
      const newline = v.indexOf('\n')
      return newline > 0 ? v.slice(0, newline) : v
    },
    formatDate(v) {
      return v.replace(/T|Z/g, ' ')
    },
    openInvoiceUpload(companyId) {
      this.selectedCompanyId = companyId;
      this.showUpload = true;
    },
    toggleGroupBySupplier() {
      this.groupBySupplier = !this.groupBySupplier;
      this.saveToLocalStorage('groupBySupplier', this.groupBySupplier);
    }
  }
}
</script>
